import type { NumberSeries, RDocumentField, RegistrationDocumentType } from './types'

export const GST_CERTIFICATE_LABEL = 'GST Certificate'
// Only for an application filed before the choice was recorded. Anything
// submitted since names the actual document.
export const REGISTRATION_CERTIFICATE_LABEL = 'Certificate of Incorporation or Udyam'
const REGISTRATION_LABELS: Partial<Record<RegistrationDocumentType, string>> = {
  coi: 'Certificate of Incorporation',
  udyam: 'Udyam Certificate',
}

/**
 * Key the answer travels under, written into the registration document's
 * `data_fields` on submit and read back off the stored application.
 *
 * The API has ONE slot for both documents ("Registration Certificate"), so
 * without recording the answer the choice dies when the modal closes: the table
 * cannot name the document, and — the part that costs a customer a second
 * rejection — the resubmit screen cannot show the matching example.
 * `data_fields` is the right carrier because it is what the flow declares about
 * a document rather than what review reads off it.
 */
export const REGISTRATION_DOCUMENT_TYPE_FIELD = 'registration_document_type'

/**
 * Which registration document a stored application carries.
 *
 * The recorded answer wins; OCR is a BACKFILL for applications filed before it
 * was recorded, never an override (root doc, failure mode 25). The identifiers
 * are exclusive — a CIN/LLPIN only appears on a Certificate of Incorporation
 * and a Udyam number only on a Udyam certificate — so where review has run they
 * settle it. Null means genuinely unknown, and the label stays hedged.
 */
export const resolveRegistrationDocumentType = (
  recorded: RegistrationDocumentType | null | undefined,
  metaInformation?: Record<string, string>,
): RegistrationDocumentType | null => {
  if (recorded === 'coi' || recorded === 'udyam') return recorded
  if (metaInformation?.udyam_registration_number) return 'udyam'
  if (metaInformation?.cin_llpin) return 'coi'
  return null
}

/** The label for a document on a stored application, resolving the answer. */
export const getStoredDocumentLabel = (document: {
  document_name: string
  meta_information?: Record<string, string>
  registration_document_type?: RegistrationDocumentType | null
}): string =>
  getGstFlowDocumentLabel(
    document.document_name,
    resolveRegistrationDocumentType(document.registration_document_type, document.meta_information),
  )

// Hoisted out of the registration document and rendered as "Business Name" until
// the GST question replaced it. Nothing collects it in this flow any more, so it
// must not be validated either.
const RETIRED_LEGAL_NAME_FIELD = 'legal name'

export const isGstDocument = (documentName: string): boolean =>
  documentName.toUpperCase().includes('GST')

/**
 * Whether "Is your business registered for GST?" drives the document slot.
 *
 * India's standard landline flow only. The 140/160 series remap both slots onto
 * TRAI-specific declarations and an application there must carry both, so the
 * question would hide a document those flows are required to send.
 */
export const isGstQuestionApplicable = (countryIso2: string, numberSeries: NumberSeries): boolean =>
  countryIso2 === 'IN' && numberSeries === 'other'

interface VisibleDocumentFieldsInput {
  documentFields: RDocumentField[] | undefined
  countryIso2: string
  numberSeries: NumberSeries
  gstRegistered: boolean | null
}

/**
 * The single document the applicant is asked for, chosen by the answer.
 *
 * Yes takes the GST certificate, No takes the registration certificate, and
 * nothing is shown until the question is answered. The requirement's own
 * legal-name field is dropped: it used to render as "Business Name" in the
 * header, and leaving it in would demand a value no input collects.
 *
 * Every consumer — the rendered card, the submit-button gate, submit-time
 * validation and the payload — must read this one list. A slot that is validated
 * but not rendered blocks submission with nothing on screen saying why (root doc,
 * failure mode 19).
 */
export const selectVisibleDocumentFields = ({
  documentFields,
  countryIso2,
  numberSeries,
  gstRegistered,
}: VisibleDocumentFieldsInput): RDocumentField[] => {
  const fields = documentFields ?? []
  if (!isGstQuestionApplicable(countryIso2, numberSeries)) return fields
  if (gstRegistered === null) return []
  return fields
    .filter((field) => isGstDocument(field.document_name) === gstRegistered)
    .map((field) => ({
      ...field,
      information: (field.information ?? []).filter(
        (info) => info.friendly_name.toLowerCase() !== RETIRED_LEGAL_NAME_FIELD,
      ),
    }))
}

/**
 * Every slot the submission must fill, which is not the one slot we render.
 *
 * IN/local/business returns two document types — Registration Certificate and
 * GST certificate — and marks BOTH `proof_required`, with no optionality flag.
 * The flow only ever asks for one file, so that file satisfies both slots and is
 * sent against each `document_type_id`.
 *
 * Rendering still uses `selectVisibleDocumentFields`: the applicant sees one
 * upload, exactly as designed. This is the payload's view, not the screen's.
 */
export const selectSubmissionDocumentFields = ({
  documentFields,
  countryIso2,
  numberSeries,
  gstRegistered,
}: VisibleDocumentFieldsInput): RDocumentField[] => {
  const fields = documentFields ?? []
  if (!isGstQuestionApplicable(countryIso2, numberSeries)) return fields
  return gstRegistered === null ? [] : fields
}

/** The API field carrying the business's legal name, required on the registration slot. */
export const BUSINESS_NAME_FIELD = 'business_name'

/** Whether a slot declares the legal-name field, which no input in this flow collects. */
export const requiresBusinessName = (documentField: RDocumentField): boolean =>
  (documentField.information ?? []).some((info) => info.field_name === BUSINESS_NAME_FIELD)

/**
 * Title for the document card. The API's generic names ("Registration
 * Certificate", and the GST slot's "Address Proof" rename) predate the question
 * and no longer describe what is being asked for once the answer picks one.
 */
export const getGstFlowDocumentLabel = (
  documentName: string,
  registrationDocumentType?: RegistrationDocumentType | null,
): string => {
  if (isGstDocument(documentName)) return GST_CERTIFICATE_LABEL
  return (
    (registrationDocumentType && REGISTRATION_LABELS[registrationDocumentType]) ??
    REGISTRATION_CERTIFICATE_LABEL
  )
}

/** Which worked example matches the answers so far. */
export const getSampleDocumentKey = (
  gstRegistered: boolean | null,
  registrationDocumentType?: RegistrationDocumentType | null,
): 'gst' | 'coi' | 'udyam' => {
  if (gstRegistered) return 'gst'
  // 'none' has no document and never reaches an upload, so it falls through to
  // the same default a saved application uses — which of the two it was is not
  // recorded, and both point at one example for now.
  return registrationDocumentType === 'udyam' ? 'udyam' : 'coi'
}

/**
 * The GST answer for a stored application.
 *
 * The recorded registration document type settles it: it is only ever written
 * when the applicant answered No. Document NAMES cannot discriminate any more —
 * every application carries both slots (see `selectSubmissionDocumentFields`),
 * so a GST-named document is present either way and `some(isGstDocument)` would
 * report Yes for every application.
 *
 * Names remain the backfill for applications submitted before the question
 * existed, where one slot was filed and nothing recorded the answer.
 */
export const deriveGstRegisteredFromDocuments = (
  documentNames: string[],
  registrationDocumentType?: RegistrationDocumentType | null,
): boolean | null => {
  if (registrationDocumentType === 'coi' || registrationDocumentType === 'udyam') return false
  return documentNames.length === 0 ? null : documentNames.some(isGstDocument)
}
